import * as React from 'react';
import { UtilityText, VisuallyHidden } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { LinkableCell } from '../table-pieces.component';

const EM_DASH = '—';

export type DateTimeCellProps = {
  /** The column's resolved value — expected to be an ISO date-time string. */
  value: unknown;
  linkTo?: string;
};

/**
 * Renders an absolute, locale-aware date-time in the viewer's local timezone
 * with a short timezone label (e.g. "Jun 22, 2026, 3:00 PM EDT"). Unlike
 * RelativeTimeCell, this reads the column's resolved `value` rather than the
 * entity's lastUpdatedDate. Unparseable values pass through unchanged (never
 * "Invalid Date").
 */
export const DateTimeCell: React.FC<DateTimeCellProps> = ({
  value,
  linkTo,
}) => {
  const { formatDate, formatMessage } = useIntl();

  // Empty value: show an em dash visually, but give assistive tech a real label
  // (the bare em dash is skipped or read literally by screen readers).
  if (value === null || value === undefined || value === '') {
    return (
      <LinkableCell linkTo={linkTo}>
        <UtilityText>
          <span aria-hidden="true">{EM_DASH}</span>
          <VisuallyHidden>
            {formatMessage({ id: 'LIST_TABLE_EMPTY_CELL_VALUE' })}
          </VisuallyHidden>
        </UtilityText>
      </LinkableCell>
    );
  }

  const date = new Date(value as string);
  const display = Number.isNaN(date.getTime())
    ? String(value)
    : formatDate(date, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      });

  return (
    <LinkableCell linkTo={linkTo}>
      <UtilityText>{display}</UtilityText>
    </LinkableCell>
  );
};
